import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useState } from "react";

import { DataTable, ExportMenu, FilterBar, type FilterSpec } from "@/components/data";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { applyFilters, parseFilters } from "@/lib/filters";

import { getSoldCostsHistory, SoldCostsError, type SoldCostsEntity, type SoldCostsKey, type SoldCostsList, type SoldCostsMonth, type SoldCostsSelection, type SoldCostsView } from "./api";
import { clampMonth, COPY, defaultSoldCostsFrom, messageFor, money, monthsBetween, moveClass, signedMoney, signedPct, SOLD_COSTS_FLOOR, sortAllowed, sortDirection, unitsLabel } from "./format";
import { ProductCostView } from "./ProductCostView";
import { heldFor, monthState, stateTitle } from "./monthState";
import { SoldCostsSkuDialog } from "./SoldCostsSkuDialog";
import { SoldCostsTieView } from "./SoldCostsTieView";

const BASE_FILTERS: FilterSpec[] = [
  { name: "q", label: "Search", kind: "text" },
  { name: "product", label: "Product", kind: "choice", options: [] },
  { name: "variant", label: "Variant", kind: "choice", options: [] },
  { name: "region", label: "Region", kind: "choice", options: ["UK", "EU", "MENA"].map((value) => ({ value, label: value })) },
  { name: "channel", label: "Channel", kind: "choice", options: ["Amazon", "Shopify", "Wholesale"].map((value) => ({ value, label: value })) },
  { name: "location", label: "Location", kind: "choice", options: [] },
];

// C4 v1.6.3: SKU to Company stay at the left edge while the month columns scroll (Bojan, 31 Oct 2026).
// Widths are fixed so each left offset equals the sum of the widths before it. The edge after Company is an inset
// shadow, not a border: Tailwind's base sets border-collapse: collapse, which can drop a border from a sticky cell.
export const PINNED_COLUMN_CLASS: Record<"sku" | "product" | "variant" | "region" | "channel" | "location" | "currency" | "company", string> = {
  sku: "sticky left-0 z-10 bg-background w-[9rem] min-w-[9rem] max-w-[9rem]",
  product: "sticky left-[9rem] z-10 bg-background w-[9rem] min-w-[9rem] max-w-[9rem]",
  variant: "sticky left-[18rem] z-10 bg-background w-[7rem] min-w-[7rem] max-w-[7rem]",
  region: "sticky left-[25rem] z-10 bg-background w-[5.5rem] min-w-[5.5rem] max-w-[5.5rem]",
  channel: "sticky left-[30.5rem] z-10 bg-background w-[6rem] min-w-[6rem] max-w-[6rem]",
  location: "sticky left-[36.5rem] z-10 bg-background w-[6.5rem] min-w-[6.5rem] max-w-[6.5rem]",
  currency: "sticky left-[43rem] z-10 bg-background w-[5rem] min-w-[5rem] max-w-[5rem]",
  company: "sticky left-[48rem] z-10 bg-background w-[5rem] min-w-[5rem] max-w-[5rem] shadow-[inset_-1px_0_0_var(--color-ks-grey-4)]",
};

export function PinnedText({ value, width }: { value: string; width: string }) {
  return <div className={`${width} truncate`} title={value}>{value}</div>;
}

const ENTITY_IDS: readonly SoldCostsEntity[] = ["LTD", "OU", "LLC"];

export function selectedFrom(value: unknown): SoldCostsSelection | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(String(value)) as Partial<SoldCostsSelection> & { entity_id?: unknown };
    if (!(parsed.xero_sku && parsed.channel_location && parsed.marketplace_id)) return null;
    const selection: SoldCostsSelection = { xero_sku: parsed.xero_sku, channel_location: parsed.channel_location, marketplace_id: parsed.marketplace_id };
    return ENTITY_IDS.includes(parsed.entity_id as SoldCostsEntity) ? { ...selection, entity_id: parsed.entity_id as SoldCostsEntity } : selection;
  } catch { return null; }
}

function option(value: string): { value: string; label: string } { return { value, label: value }; }

function monthValue(key: SoldCostsKey & { months: SoldCostsMonth[] }, month: string): SoldCostsMonth | undefined {
  return key.months.find((entry) => entry.month === month);
}

export function soldCostsCsv(data: SoldCostsList, months: string[], view: SoldCostsView = "fee"): string {
  const hasStates = months.some((month) => data.keys.some((key) => monthValue(key, month)?.waiting_for_sheet));
  const stateFor = (key: SoldCostsList["keys"][number], month: string): string => (monthValue(key, month)?.waiting_for_sheet ? "waiting for sheet" : "");
  const rows: Array<Array<string | number>> = [
    ["SKU", "Product", "Variant", "Region", "Channel", "Location", "Currency", "Company", ...months, ...(hasStates ? months.map((month) => `${month} state`) : [])],
    ...data.keys.map((key) => [key.xero_sku, key.product, key.variant, key.region, key.channel, key.location, key.currency ?? "", key.entity_id, ...months.map((month) => {
      const value = monthValue(key, month);
      if (!value || heldFor(data.held, key.entity_id, month)) return "";
      return view === "change" ? value.change ?? "" : view === "pct" ? value.change_pct ?? "" : value.per_unit ?? "";
    }), ...(hasStates ? months.map((month) => stateFor(key, month)) : [])]),
  ];
  return rows.map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",")).join("\n");
}

export function exportSoldCostsCsv(data: SoldCostsList | undefined, months: string[], view: SoldCostsView): void {
  if (!data) return;
  const url = URL.createObjectURL(new Blob([soldCostsCsv(data, months, view)], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `sold-costs-history-${data.from}-${data.to}.csv`;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function SoldCostsListView({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const values = parseFilters(search);
  const today = new Date();
  // C4 v1.6.8 (DEC-1593 PB-121 B): the default period ends on last month, so every default column is a closed month.
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastMonth = `${lastMonthStart.getFullYear()}-${String(lastMonthStart.getMonth() + 1).padStart(2, "0")}`;
  const [floor, setFloor] = useState(SOLD_COSTS_FLOOR);
  const to = String(search.sc_to || lastMonth);
  const from = clampMonth(String(search.sc_from || defaultSoldCostsFrom(to, floor)), floor);
  const view: SoldCostsView = search.sc_view === "change" || search.sc_view === "pct" ? search.sc_view : "fee";
  const page = Math.max(1, Number(search.sc_page ?? 1));
  const selected = selectedFrom(search.sc_key);
  const months = monthsBetween(from, to);
  const requestedSort = String(search.sc_sort ?? "sku");
  const sort = sortAllowed(requestedSort, months) ? requestedSort : "sku";
  const direction: "asc" | "desc" = search.sc_dir === "desc" ? "desc" : "asc";
  const tooWide = months.length > 36;
  const emptyPeriod = to < from;
  const set = (patch: Record<string, unknown>) => void navigate({ to: ".", search: { ...search, ...patch }, replace: true });

  const list = useQuery({
    queryKey: ["pcogs", "sold-costs", "list", from, to, values, page, view, sort, direction],
    queryFn: () => getSoldCostsHistory({
      from, to, page, view, sort, dir: direction,
      q: values.q as string, product: values.product as string, variant: values.variant as string,
      region: values.region as string, channel: values.channel as string, location: values.location as string,
    }),
    enabled: !tooWide && !emptyPeriod,
    retry: 1,
  });
  useEffect(() => {
    if (list.data?.data.floor) setFloor(list.data.data.floor);
  }, [list.data?.data.floor]);

  if (tooWide) return <p role="alert" className="text-ks-danger">{COPY.range}</p>;
  if (list.error) {
    const error = list.error as SoldCostsError;
    if (error.status === 404 || error.status === 405) return <p className="text-ks-ink">{COPY.unavailable}</p>;
    return <p role="alert" className="text-ks-danger">{messageFor(error)}</p>;
  }

  const data = list.data?.data;
  const displayedFrom = clampMonth(String(search.sc_from || defaultSoldCostsFrom(to, data?.floor ?? floor)), data?.floor ?? floor);
  const displayedMonths = monthsBetween(displayedFrom, to);
  const filters = BASE_FILTERS.map((filter) => {
    if (filter.name === "product") return { ...filter, options: (data?.filters.products ?? []).map(option) };
    if (filter.name === "variant") return { ...filter, options: (data?.filters.variants ?? []).map(option) };
    if (filter.name === "location") return { ...filter, options: (data?.filters.locations ?? []).map(option) };
    return filter;
  });
  type Row = SoldCostsList["keys"][number];
  const columns: ColumnDef<Row, unknown>[] = [
    { id: "sku", header: "SKU", cell: ({ row }) => {
      const item = row.original;
      return <Button variant="link" className="h-auto max-w-[8rem] px-0" title={item.xero_sku} data-nonmutating onClick={() => set({ sc_key: JSON.stringify({ xero_sku: item.xero_sku, channel_location: item.channel_location, marketplace_id: item.marketplace_id, entity_id: item.entity_id }) })}><span className="truncate">{item.xero_sku}</span></Button>;
    }, meta: { sortable: true, className: PINNED_COLUMN_CLASS.sku } },
    { id: "product", accessorKey: "product", header: "Product", cell: ({ row }) => <PinnedText value={row.original.product} width="w-[8rem]" />, meta: { sortable: true, className: PINNED_COLUMN_CLASS.product } },
    { id: "variant", accessorKey: "variant", header: "Variant", cell: ({ row }) => <PinnedText value={row.original.variant} width="w-[6rem]" />, meta: { sortable: true, className: PINNED_COLUMN_CLASS.variant } },
    { id: "region", accessorKey: "region", header: "Region", cell: ({ row }) => <PinnedText value={row.original.region} width="w-[4.5rem]" />, meta: { sortable: true, className: PINNED_COLUMN_CLASS.region } },
    { id: "channel", accessorKey: "channel", header: "Channel", cell: ({ row }) => <PinnedText value={row.original.channel} width="w-[5rem]" />, meta: { sortable: true, className: PINNED_COLUMN_CLASS.channel } },
    { id: "location", accessorKey: "location", header: "Location", cell: ({ row }) => <PinnedText value={row.original.location} width="w-[5.5rem]" />, meta: { sortable: true, className: PINNED_COLUMN_CLASS.location } },
    { id: "currency", header: "Currency", cell: ({ row }) => <PinnedText value={row.original.currency ?? "—"} width="w-[4rem]" />, meta: { className: PINNED_COLUMN_CLASS.currency } },
    { id: "company", header: "Company", cell: ({ row }) => <PinnedText value={row.original.entity_id} width="w-[4rem]" />, meta: { className: PINNED_COLUMN_CLASS.company } },
    ...displayedMonths.map((month, monthIndex): ColumnDef<Row, unknown> => ({
      id: month,
      header: month,
      cell: ({ row }) => {
      const key = row.original;
      const value = monthValue(key, month);
      const held = heldFor(data?.held, key.entity_id, month);
      const state = monthState({ ...value, held: held !== undefined });
      if (view === "fee") {
        if (state === "held") return "—";
        if (!value || (value.per_unit === null && state !== "waiting")) return "—";
        const text = money(value.per_unit, key.currency);
        const badge = state === "waiting" ? "waiting for sheet" : state === "open" ? "open" : null;
        const title = state === "waiting" || state === "history" ? stateTitle(value) : unitsLabel(key, value);
        return <TooltipProvider><Tooltip><TooltipTrigger asChild><span className={moveClass(value.change)} title={title}>{text}{badge && <Badge variant="outline" className="ml-1" title={state === "open" ? "open" : undefined}>{badge}</Badge>}</span></TooltipTrigger><TooltipContent>{unitsLabel(key, value)}</TooltipContent></Tooltip></TooltipProvider>;
      }
      const displayed = view === "change" ? signedMoney(value?.change, key.currency) : signedPct(value?.change_pct);
      if (state === "held" || !value) return <span />;
      if (displayed === "") return state === "waiting" ? <span title={stateTitle(value)}><Badge variant="outline">waiting for sheet</Badge></span> : <span />;
      const previous = monthIndex > 0 ? monthValue(key, displayedMonths[monthIndex - 1]) : undefined;
      const badge = state === "waiting" ? "waiting for sheet" : previous?.waiting_for_sheet ? "vs partial month" : null;
      const title = state === "waiting" ? stateTitle(value) : previous?.waiting_for_sheet ? "Change against a month without Cost of Product." : undefined;
      return <span className={moveClass(value.change)} title={title}>{displayed}{badge && <Badge variant="outline" className="ml-1">{badge}</Badge>}</span>;
    }, meta: { sortable: true } })),
  ];
  const legend = view === "fee" ? COPY.legendFee : view === "change" ? COPY.legendChange : COPY.legendPct;

  return <div className="flex flex-col gap-4">
    <p className="text-sm text-ks-grey-1">{COPY.header}</p>
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex flex-col text-sm">From<Input data-nonmutating type="month" min={data?.floor ?? floor} value={displayedFrom} onChange={(event) => set({ sc_from: event.target.value, sc_page: 1 })} /></label>
      <label className="flex flex-col text-sm">To<Input data-nonmutating type="month" min={data?.floor ?? floor} value={to} onChange={(event) => set({ sc_to: event.target.value, sc_page: 1 })} /></label>
      <ExportMenu formats={[{ key: "csv", label: "CSV" }]} decision="view" onExport={() => exportSoldCostsCsv(data, displayedMonths, view)} />
    </div>
    {Boolean(search.sc_from) && String(search.sc_from) < (data?.floor ?? floor) && !emptyPeriod && <p role="status" className="text-sm text-ks-ink">{COPY.floorNotice(data?.floor ?? floor)}</p>}
    <FilterBar filters={filters} values={values} onChange={(next) => void navigate({ to: ".", search: { ...applyFilters(search, next), sc_page: 1 }, replace: true })} />
    <div role="radiogroup" aria-label="Sold Costs history view" className="flex flex-wrap gap-2">
      {(["fee", "change", "pct"] as const).map((item) => <Button key={item} variant={view === item ? "default" : "outline"} role="radio" aria-checked={view === item} data-nonmutating onClick={() => set({ sc_view: item, sc_page: 1 })}>{item === "fee" ? COPY.viewFee : item === "change" ? COPY.viewChange : COPY.viewPct}</Button>)}
    </div>
    <p className="text-sm text-ks-grey-1">{legend} · {COPY.sortHint}</p>
    {emptyPeriod ? <p role="status" className="text-ks-ink">{COPY.periodEmpty(displayedFrom, to, data?.floor ?? floor)}</p> : <DataTable columns={columns} rows={data?.keys ?? []} pending={list.isPending} sort={{ id: sort, direction }} onSort={(id) => set({ sc_sort: id, sc_dir: sortDirection(sort, direction, id), sc_page: 1 })} page={{ pageIndex: page - 1, pageSize: 50, rowCount: data?.total ?? 0 }} onPageChange={(index) => set({ sc_page: index + 1 })} emptyMessage={COPY.empty} caption={`Sold Costs history, ${areaName}`} />}
    {selected && !emptyPeriod && <SoldCostsSkuDialog selected={selected} from={displayedFrom} to={to} onClose={() => set({ sc_key: undefined })} />}
  </div>;
}

export function SoldCostsTab({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const mode = search.sc_mode === "product_cost" ? "product_cost" : search.sc_mode === "pnl_tie" ? "pnl_tie" : "list";
  const setMode = (next: "list" | "product_cost" | "pnl_tie") => void navigate({
    to: ".",
    search: { ...search, sc_mode: next === "list" ? undefined : next },
    replace: true,
  });

  return <div className="flex flex-col gap-4">
    <div role="radiogroup" aria-label="Sold Costs view" className="flex flex-wrap gap-2">
      <Button variant={mode === "list" ? "default" : "outline"} role="radio" aria-checked={mode === "list"} data-nonmutating onClick={() => setMode("list")}>Sold-COGS per unit</Button>
      <Button variant={mode === "product_cost" ? "default" : "outline"} role="radio" aria-checked={mode === "product_cost"} data-nonmutating onClick={() => setMode("product_cost")}>Cost of Product</Button>
      <Button variant={mode === "pnl_tie" ? "default" : "outline"} role="radio" aria-checked={mode === "pnl_tie"} data-nonmutating onClick={() => setMode("pnl_tie")}>Tie to the P&amp;L</Button>
    </div>
    {mode === "pnl_tie" ? <SoldCostsTieView /> : mode === "product_cost" ? <ProductCostView /> : <SoldCostsListView areaName={areaName} />}
  </div>;
}
